// Thin hook around is_platform_admin RPC with shared module-level cache keyed on user.id.
// Cache + in-flight maps are exported so PlatformGuard and PostLoginRedirect reuse the
// same single RPC result for a session.
// Returns: true | false | null (null = loading or no user).
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export const platformAdminCache = new Map<string, boolean>();
export const platformAdminInFlight = new Map<string, Promise<boolean>>();

export function resolvePlatformAdmin(userId: string): Promise<boolean> {
  if (platformAdminCache.has(userId)) {
    return Promise.resolve(platformAdminCache.get(userId)!);
  }
  const existing = platformAdminInFlight.get(userId);
  if (existing) return existing;
  const p = (async () => {
    try {
      const { data, error } = await supabase.rpc('is_platform_admin', { _user: userId });
      const ok = !error && data === true;
      // Only cache positive admin checks. A false result can become stale if a
      // platform-admin row is restored while the app shell remains open, which
      // would send the user through the employee invite gate incorrectly.
      if (ok) platformAdminCache.set(userId, true);
      else platformAdminCache.delete(userId);
      return ok;
    } catch {
      platformAdminCache.delete(userId);
      return false;
    } finally {
      platformAdminInFlight.delete(userId);
    }
  })();
  platformAdminInFlight.set(userId, p);
  return p;
}

export function usePlatformAdmin(): boolean | null {
  const { user, isLoading } = useAuth();
  const [state, setState] = useState<boolean | null>(() => {
    if (user?.id && platformAdminCache.has(user.id)) return platformAdminCache.get(user.id)!;
    return null;
  });

  useEffect(() => {
    if (isLoading || !user?.id) return;
    const key = user.id;
    if (platformAdminCache.has(key)) { setState(platformAdminCache.get(key)!); return; }
    let cancelled = false;
    resolvePlatformAdmin(key).then((ok) => { if (!cancelled) setState(ok); });
    return () => { cancelled = true; };
  }, [user?.id, isLoading]);

  return state;
}
